import { LockKeyhole } from "lucide-react";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import {
  IgAvatar,
  IgButton,
  IgLink,
  IgTypography,
} from "@intelligems/ig-theme";
import { Stack, useTheme } from "@mui/material";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { selectedIsShopLogin } from "@src/store/slices/appSettings";
import { logoutWithWorker } from "@src/utils";
import { appListing } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import { IgErrorBoundary } from "../Utils/IgErrorBoundary";

import { ShopLogin } from "./ShopLogin";
import { UserPasswordLogin } from "./UserPasswordLogin";

const PREFIX = "LoginPage";

const classes = {
  avatar: `${PREFIX}-avatar`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.avatar}`]: {
    margin: theme.spacing(1),
    backgroundColor: theme.igBackgroundColors.accentSolid,
    width: 56,
    height: 56,
    [theme.breakpoints.down("sm")]: {
      width: 48,
      height: 48,
    },
  },
}));

export default function LoginPage() {
  const theme = useTheme();
  const isShopLogin = useSelector(selectedIsShopLogin);
  useEffect(() => {
    if (window.location.href.includes("logout")) {
      logoutWithWorker();
    }
  }, []);

  return (
    <StyledContainer
      maxWidth="xs"
      sx={{
        display: "grid",
        minHeight: "100dvh",
        gridTemplateRows: "1fr min-content 1fr",
      }}
    >
      <CssBaseline />

      <Stack alignItems="center" justifyContent="end">
        <IgAvatar variant={"circular"} className={classes.avatar}>
          <LockKeyhole color={theme.igIconColors.dark} />
        </IgAvatar>

        <IgTypography variant={"h1"}>Sign in</IgTypography>

        <IgButton
          onClick={() => {
            openNewTab(appListing, "Login Page - Download App");
          }}
          fullWidth
          variant={"text"}
          sx={{
            minHeight: 44,
            marginBlock: 1,
            padding: (theme) => theme.spacing(1.5, 2),
          }}
        >
          Don't have an account? Download Intelligems now!
        </IgButton>
      </Stack>

      <IgErrorBoundary>
        {isShopLogin ? <ShopLogin /> : <UserPasswordLogin />}
      </IgErrorBoundary>

      <Stack alignItems={"center"} mt={2}>
        <IgTypography variant="body2" color={theme.palette.text.secondary}>
          {"Copyright © "}
          <IgLink variant="body2" href="intelligems.io/">
            Intelligems, Inc
          </IgLink>{" "}
          {new Date().getFullYear()}
          {"."}
        </IgTypography>
      </Stack>
    </StyledContainer>
  );
}
